import { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from "recharts";
import { TrendingUp, TrendingDown, Clock, CheckCircle, AlertTriangle } from "lucide-react";
import { groundOpsApi } from "@/api";

const COLORS = ["#f97316", "#ef4444", "#3b82f6", "#22c55e", "#8b5cf6"];

export default function StationDashboard() {
  const [perf, setPerf] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    groundOpsApi.getPerformance().then(d => setPerf(d)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-10 text-center text-slate-500 animate-pulse">Loading performance data...</div>;
  if (!perf) return <div className="p-10 text-center text-red-400">Failed to load</div>;

  return (
    <div className="p-5 space-y-5">
      <div className="flex items-center gap-2 mb-1">
        <h1 className="text-base font-black uppercase tracking-widest text-white">Station Performance</h1>
        <span className="text-[9px] text-slate-600 font-mono">Today's ground operations</span>
      </div>

      {/* ── KPI Row ──────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { label: "Fleet OTP",       value: `${perf.fleet_otp_pct}%`,   icon: CheckCircle,  color: perf.fleet_otp_pct >= 80 ? "text-green-400" : "text-amber-400" },
          { label: "Avg TAT",         value: `${perf.avg_tat_mins}m`,     icon: Clock,        color: "text-sky-400"   },
          { label: "Movements",       value: perf.total_movements,        icon: TrendingUp,   color: "text-orange-400" },
          { label: "Active Alerts",   value: perf.active_alerts,          icon: AlertTriangle, color: perf.active_alerts > 0 ? "text-red-400" : "text-green-400" },
        ].map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="bg-[#0c1929] border border-slate-700/30 rounded-lg px-4 py-4" data-testid={`perf-kpi-${label.toLowerCase().replace(/\s/g,"-")}`}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-[9px] uppercase tracking-widest text-slate-500">{label}</span>
              <Icon size={12} className={color} />
            </div>
            <div className={`text-2xl font-black font-mono ${color}`}>{value}</div>
          </div>
        ))}
      </div>

      {/* ── Charts row ───────────────────────────────────────────────────── */}
      <div className="grid grid-cols-3 gap-4">
        {/* OTP by Station */}
        <div className="col-span-2 bg-[#0c1929] border border-slate-700/30 rounded-xl p-4">
          <h3 className="text-[10px] uppercase tracking-widest text-slate-500 mb-3 font-bold">On-Time Performance by Station</h3>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={perf.stations} barSize={20}>
              <XAxis dataKey="station" tick={{ fill: "#64748b", fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: "#64748b", fontSize: 10 }} axisLine={false} tickLine={false} domain={[0, 100]} unit="%" />
              <Tooltip
                contentStyle={{ background: "#0c1929", border: "1px solid #1e3a5f", borderRadius: 6, fontSize: 11 }}
                formatter={(v) => [`${v}%`, "OTP"]}
              />
              <Bar dataKey="otp_pct" radius={[3, 3, 0, 0]}>
                {perf.stations.map((s, i) => (
                  <Cell key={i} fill={s.otp_pct >= 85 ? "#22c55e" : s.otp_pct >= 70 ? "#f59e0b" : "#ef4444"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Delay Causes */}
        <div className="bg-[#0c1929] border border-slate-700/30 rounded-xl p-4">
          <h3 className="text-[10px] uppercase tracking-widest text-slate-500 mb-3 font-bold">Delay Causes</h3>
          <ResponsiveContainer width="100%" height={130}>
            <PieChart>
              <Pie data={perf.delay_causes} dataKey="count" nameKey="cause" cx="50%" cy="50%" outerRadius={55} innerRadius={30}>
                {perf.delay_causes.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={{ background: "#0c1929", border: "1px solid #1e3a5f", borderRadius: 6, fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
          <div className="space-y-1 mt-2">
            {perf.delay_causes.map((d, i) => (
              <div key={d.cause} className="flex items-center gap-2 text-[9px] text-slate-400">
                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: COLORS[i % COLORS.length] }} />
                <span className="flex-1">{d.cause}</span>
                <span className="font-mono">{d.pct}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── TAT Trend ────────────────────────────────────────────────────── */}
      <div className="bg-[#0c1929] border border-slate-700/30 rounded-xl p-4">
        <h3 className="text-[10px] uppercase tracking-widest text-slate-500 mb-3 font-bold">TAT & OTP Trend — Last 7 Days</h3>
        <ResponsiveContainer width="100%" height={120}>
          <LineChart data={perf.trend}>
            <XAxis dataKey="day" tick={{ fill: "#64748b", fontSize: 10 }} axisLine={false} tickLine={false} />
            <YAxis yAxisId="tat" orientation="left" tick={{ fill: "#64748b", fontSize: 10 }} axisLine={false} tickLine={false} domain={[40, 70]} />
            <YAxis yAxisId="otp" orientation="right" tick={{ fill: "#64748b", fontSize: 10 }} axisLine={false} tickLine={false} domain={[60, 100]} unit="%" />
            <Tooltip contentStyle={{ background: "#0c1929", border: "1px solid #1e3a5f", borderRadius: 6, fontSize: 11 }} />
            <Line yAxisId="tat" type="monotone" dataKey="tat_mins" stroke="#f97316" strokeWidth={2} dot={false} name="TAT (min)" />
            <Line yAxisId="otp" type="monotone" dataKey="otp_pct" stroke="#22c55e" strokeWidth={2} dot={false} name="OTP %" />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* ── Station Table ─────────────────────────────────────────────────── */}
      <div className="bg-[#0c1929] border border-slate-700/30 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-700/30">
          <h3 className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Station Summary</h3>
        </div>
        <table className="w-full text-[11px]" data-testid="station-table">
          <thead>
            <tr className="border-b border-slate-700/20">
              {["Station", "Movements", "OTP %", "Avg TAT", "Delayed", "At Risk"].map(h => (
                <th key={h} className="px-4 py-2.5 text-left text-[9px] text-slate-500 uppercase tracking-wider font-bold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {perf.stations.map((s, i) => (
              <tr key={s.station} className={`border-b border-slate-700/20 hover:bg-white/[0.02] transition ${i === 0 ? "bg-green-500/5" : ""}`}>
                <td className="px-4 py-3 font-mono font-bold text-white">{s.station}</td>
                <td className="px-4 py-3 text-slate-400">{s.movements}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="w-16 h-1.5 bg-slate-700/50 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${s.otp_pct >= 85 ? "bg-green-500" : s.otp_pct >= 70 ? "bg-amber-500" : "bg-red-500"}`} style={{ width: `${s.otp_pct}%` }} />
                    </div>
                    <span className={s.otp_pct >= 85 ? "text-green-400 font-bold" : s.otp_pct >= 70 ? "text-amber-400 font-bold" : "text-red-400 font-bold"}>{s.otp_pct}%</span>
                  </div>
                </td>
                <td className="px-4 py-3 font-mono text-slate-300">{s.avg_tat_mins}m</td>
                <td className="px-4 py-3"><span className={`font-mono ${s.delayed > 0 ? "text-red-400" : "text-slate-600"}`}>{s.delayed}</span></td>
                <td className="px-4 py-3"><span className={`font-mono ${s.at_risk > 0 ? "text-amber-400" : "text-slate-600"}`}>{s.at_risk}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
